import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Banner, Button, categoriaLabel, type BadgeTone } from '@aions/ui-primitives';
import {
  comiteCompetencia,
  estadoLabelComite,
  resolverPrazo,
  FERIADOS_NACIONAIS,
  RITO_STAGES,
  RITO_STAGE_LABEL,
  ESTADOS_EM_APURACAO,
  type PrazoResultado,
  type RitoStage,
} from '@aions/core';
import { apiFetch, logout, getStoredSession } from '../lib/auth-client';
import { useSession } from '../contexts/SessionContext';

interface CaseListItem {
  caseId: string;
  categoria: string;
  createdWeek: string;
  stateKind: string;
  attachmentsCount: number;
  // Prazos Fase 2 — anchors projetados para o badge de prazo (resolverPrazo).
  openedAt: number | null;
  classifiedAt: number | null;
  decisaoEmittedAt: number | null;
  investigacaoProrrogada: boolean;
  // Conformidade (Div. 3): âncora da CONCLUSÃO (close_oitivas). Ainda não projetada
  // pelo backend (pende decisão de fonte de timestamp preciso — audit é semanal por
  // anonimato); ausente → conclusão degrada sem badge. Análise prévia usa classifiedAt.
  apuracaoEncerradaAt?: number | null;
  /**
   * D2 — "a última mensagem do canal é do denunciante", derivado no servidor
   * (docs/LEVANTAMENTO-CHAT-2026-07-28.md §D2). Antes disto o comitê não tinha sinal
   * NENHUM de mensagem nova: o `unseenPhaseChange` do banco é do denunciante, e o
   * próprio POST dele o apaga. Descobrir que alguém respondeu exigia abrir caso a caso.
   *
   * OPCIONAL de propósito: a projeção é do handler da API e pode não estar no ar
   * ainda. `undefined` em TODOS os casos ⇒ a tela fica idêntica ao que era (sem
   * marcador, sem filtro). Nada de controle morto filtrando para lista vazia.
   *
   * ⚠️ NÃO cobre caso `Encerrada`: `lookupAllForComite` filtra `Encerrada`, então uma
   * resposta pós-encerramento continua invisível aqui. D2 e D1 se completam — este
   * marcador resolve o caso ATIVO cuja resposta ninguém abriu; o caso encerrado que
   * aceita resposta e some da lista é o D1.
   */
  aguardandoComite?: boolean;
  /** Fase canônica do rito (`RITO_STAGES` do core). Usada para ORDENAR e para o
   *  filtro por status. Opcional: projeção nova — ausente ⇒ o caso cai no fim da
   *  ordenação e o filtro de status o ignora, em vez de a tela quebrar. */
  stage?: RitoStage | null;
  /** A conta logada tem ato de validação pendente neste caso (qualquer etapa). */
  pendenteViewer?: boolean;
  /** Outro membro tem. */
  pendenteOutros?: boolean;
}

/** Filtro de pendência da tela inicial. `todas` = sem filtro. */
type FiltroPendencia = 'todas' | 'minhas' | 'outros';

/** Nomes dos meses em pt-BR. Literais e não `Intl`: o valor do `<option>` é o NÚMERO
 *  (1..12) e só o rótulo é texto — derivar de `Intl` mudaria a capitalização conforme
 *  o locale do navegador e o filtro de agosto viraria "agosto" ou "Agosto" sem
 *  critério. */
const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
] as const;

/**
 * O caso cai no período pedido? Data exata, mês e ano são INDEPENDENTES e cumulativos
 * (data 12/08 + ano 2026 é redundante, mas coerente; mês 8 + ano 2026 é o uso real).
 *
 * ⚠️ Filtro por período com `openedAt` AUSENTE devolve `false` — o caso sai da lista.
 * É deliberado e é o único ponto onde um card desaparece: quem pede "abertas em agosto"
 * não pode receber um caso cuja data não se conhece, porque a resposta seria uma
 * afirmação falsa sobre a data. Sem filtro de período ativo, nada é escondido.
 *
 * Fuso do navegador, igual ao resto da tela (`dataHora`): quem filtra é membro do
 * comitê no Brasil, e comparar em UTC faria "12/08" excluir o que ele vê como 12/08.
 */
function dentroDoPeriodo(
  openedAt: number | null | undefined,
  data: string,
  mes: string,
  ano: string,
): boolean {
  if (data === '' && mes === '' && ano === '') return true;
  if (openedAt === null || openedAt === undefined) return false;
  const d = new Date(openedAt);
  if (data !== '') {
    // `yyyy-mm-dd` do <input type="date"> comparado com a data LOCAL do caso.
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate(),
    ).padStart(2, '0')}`;
    if (iso !== data) return false;
  }
  if (mes !== '' && d.getMonth() + 1 !== Number(mes)) return false;
  if (ano !== '' && d.getFullYear() !== Number(ano)) return false;
  return true;
}

/** Tom do badge de prazo a partir do status (paridade com o detalhe). */
function prazoTone(status: PrazoResultado['status']): BadgeTone {
  if (status === 'vencido') return 'coral';
  if (status === 'vencendo') return 'amber';
  return 'neutral';
}

/** Mapeia o estado da denúncia para o tom do Badge (visual, sem semântica jurídica). */
function stateTone(kind: string): BadgeTone {
  switch (kind) {
    case 'Aberta':
    case 'Triagem':
      return 'amber';
    case 'Decisao':
    case 'Encerrada':
      return 'brand';
    case 'Recursal':
      return 'coral';
    default:
      return 'neutral';
  }
}

export function DashboardPage({
  onSelectCase,
  onLogout,
}: {
  onSelectCase: (id: string) => void;
  onLogout: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { session, setSession } = useSession();
  const [cases, setCases] = useState<CaseListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [soAguardando, setSoAguardando] = useState(false);
  const [busca, setBusca] = useState('');
  /** `apuracao` NÃO é uma etapa: é um AGRUPAMENTO de etapas (Instrução → decisão
   *  pendente). Fica no mesmo select porque é a mesma pergunta ("em que ponto está?"),
   *  e o rótulo diz o intervalo para ninguém achar que é uma fase do rito. */
  const [filtroStage, setFiltroStage] = useState<RitoStage | 'todas' | 'apuracao'>('todas');
  const [filtroPend, setFiltroPend] = useState<FiltroPendencia>('todas');
  /** Data exata (`yyyy-mm-dd`), mês (`1..12`) e ano (`yyyy`) — vazios = sem filtro. */
  const [filtroData, setFiltroData] = useState('');
  const [filtroMes, setFiltroMes] = useState('');
  const [filtroAno, setFiltroAno] = useState('');

  const filtrosAtivos =
    busca.trim() !== '' ||
    filtroStage !== 'todas' ||
    filtroPend !== 'todas' ||
    filtroData !== '' ||
    filtroMes !== '' ||
    filtroAno !== '' ||
    soAguardando;

  /** Algum filtro ALÉM do `soAguardando` está ativo? Decide a copy do estado vazio. */
  const buscaOuPeriodoAtivo =
    busca.trim() !== '' ||
    filtroStage !== 'todas' ||
    filtroPend !== 'todas' ||
    filtroData !== '' ||
    filtroMes !== '' ||
    filtroAno !== '';

  const limparFiltros = (): void => {
    setBusca('');
    setFiltroStage('todas');
    setFiltroPend('todas');
    setFiltroData('');
    setFiltroMes('');
    setFiltroAno('');
    setSoAguardando(false);
  };

  /** Recarrega a lista sem sair da página.
   *
   *  Extraído do `useEffect` para poder ser chamado pelo botão de atualizar.
   *  A lógica de erro é a MESMA — reescrevê-la no botão criaria duas cópias da
   *  regra que distingue `policy_not_accepted` de 403 comum e de falha de rede,
   *  e elas divergiriam no primeiro ajuste.
   *
   *  ⚠️ Isto NÃO é só conveniência: no comitê, F5 é o gatilho do bug da KEK
   *  perdida (abrir caso → F5 → `falha_decrypt`, com a cura sendo relogar).
   *  Um botão que atualiza sem recarregar a página evita o gesto que dispara o
   *  defeito — o `SessionContext` sobrevive, e a KEK junto. */
  // `setSession` entra por REF, não por dependência do useCallback.
  //
  // Medido: com um `setSession` recriado a cada render, `carregar` muda de
  // identidade, o `useEffect` re-dispara e vira LOOP DE REQUISIÇÃO — 7 chamadas
  // onde deviam ser 2. O `SessionContext` real usa `useCallback` e é estável,
  // então o loop não aparece no app hoje; mas fazer a corretude do dashboard
  // depender de um detalhe de implementação do provider é frágil demais para um
  // efeito que dispara fetch. Com a ref, `carregar` é estável por construção.
  const setSessionRef = useRef(setSession);
  setSessionRef.current = setSession;

  const carregar = useCallback(async (opts?: { cancelado?: () => boolean }): Promise<void> => {
    const cancelled = opts?.cancelado ?? ((): boolean => false);
    try {
      const resp = await apiFetch('/api/comite/cases');
      if (cancelled()) return;
      if (!resp.ok) {
        // BUG 1 — distinguir estado-esperado de erro técnico.
        const body = (await resp.json().catch(() => ({}))) as { error?: string };
        if (resp.status === 403 && body.error === 'policy_not_accepted') {
          // Reusa o PolicyAcceptanceModal (App-level: dispara com
          // session.user.policyAccepted === false). Lê fresh pra não re-disparar.
          const s = getStoredSession();
          if (s && s.user.policyAccepted !== false) {
            setSessionRef.current({ ...s, user: { ...s.user, policyAccepted: false } });
          }
        } else if (resp.status === 403) {
          setError('Sem permissão para acessar.');
        } else {
          setError('Falha ao carregar casos.');
        }
        return;
      }
      const data = (await resp.json()) as { cases: CaseListItem[] };
      if (cancelled()) return;
      // Limpa erro anterior SÓ no sucesso: se a atualização falhar, a mensagem
      // antiga continua de pé em vez de sumir e deixar a tela parecendo ok.
      setError(null);
      setCases(data.cases);
    } catch {
      if (!cancelled()) setError('Falha de rede.');
    }
  }, []);

  // 🔴 CARREGA DE NOVO QUANDO A LGP-02 É ACEITA (bug relatado pelo operador, 17/08/2026).
  //
  // Sintoma: "quando eu abro de primeira o canal ele fica carregando infinitamente as
  // denúncias, ele só exibe realmente quando eu clico em atualizar".
  //
  // Mecanismo, e ele depende de TRÊS peças se alinhando:
  //   1. no primeiro login o servidor responde **403 `policy_not_accepted`** a
  //      `/api/comite/cases`;
  //   2. `carregar` trata esse 403 abrindo o modal de aceite e **sai sem tocar `cases`**,
  //      que continua `null`;
  //   3. `cases === null` é exatamente a condição do skeleton (`aria-busy`), e o efeito
  //      dependia só de `[carregar]` — estável por construção, de propósito, para não
  //      virar laço de requisição. Estável significa que **ele nunca roda de novo**.
  //
  // Resultado: aceitar a política liberava o servidor e não recarregava a tela. O skeleton
  // ficava para sempre, e só o botão ATUALIZAR — que chama `carregar` na mão — resolvia.
  // Cada peça isolada está correta; o defeito é a composição.
  //
  // ⚠️ `policyAccepted === false` SAI CEDO em vez de buscar: o modal está na frente e o
  // fetch só tomaria 403 outra vez. Não é otimização — sem o early return, a mudança de
  // dependência faria o efeito re-disparar no próprio 403 que ele acabou de tratar.
  //
  // O ramo `policy_not_accepted` do `carregar` CONTINUA necessário: a sessão pode dizer
  // `true` e o servidor discordar (bump da versão da LGP-02 invalida aceites antigos), e aí
  // é ele quem abre o modal.
  // `session` é anulável no tipo do contexto (o provider zera na expiração). Optional
  // chaining, e NÃO `!`: sessão nula devolve `undefined`, que não é `false`, então o efeito
  // segue tentando carregar — e quem barra é o 401 do `apiFetch`, com o aviso próprio dele.
  const policyAceita = session?.user.policyAccepted;
  useEffect(() => {
    if (policyAceita === false) return;
    let cancelled = false;
    void carregar({ cancelado: () => cancelled });
    return () => {
      cancelled = true;
    };
  }, [carregar, policyAceita]);

  /** Clique no botão de atualizar. Estado próprio para o botão poder mostrar
   *  que está trabalhando — sem isso, uma lista que volta igual (nenhuma
   *  denúncia nova) fica indistinguível de um clique que não funcionou. */
  const [atualizando, setAtualizando] = useState(false);
  const handleAtualizar = async (): Promise<void> => {
    setAtualizando(true);
    try {
      await carregar();
    } finally {
      setAtualizando(false);
    }
  };

  // O CONTROLE só existe se a API suporta o sinal — e "suporta" é o campo estar
  // PRESENTE (mesmo `false`), não ser `true`. Se dependesse de haver algum caso
  // aguardando, o filtro apareceria e sumiria sozinho conforme o comitê responde, e o
  // membro concluiria que a funcionalidade quebrou. Presença ≠ valor.
  const suportaSinal = cases?.some((c) => c.aguardandoComite !== undefined) ?? false;

  // LT-01 — ORDEM EXPLÍCITA, mais recente primeiro.
  //
  // Antes daqui NINGUÉM ordenava: `listCases()` no SupabaseRepo faz `.select()`
  // sem `.order()`, e esta tela fazia `.map()` sem `.sort()`. A lista parecia
  // cronológica só porque o Postgres tende a devolver na ordem do índice da PK, e
  // a PK (`case_id`) era time-prefixed — a ordenação era EFEITO COLATERAL do
  // vazamento de tempo que o LT-01 remove. Sem esta linha, tirar o tempo do
  // caseId embaralharia os cards em silêncio, e ninguém ligaria uma coisa à outra.
  //
  // `openedAt` é o campo — nenhum conceito novo. (Ele era truncado ao dia UTC na
  // origem; desde 2026-08-13, por decisão do operador, carrega o instante. A
  // ordenação só melhora: empates dentro do mesmo dia deixaram de existir.)
  // `null` (estado Encerrada perde o campo) vai para o FIM, nunca é descartado:
  // filtrar aqui seria perda silenciosa de card.
  // ── ORDEM: fase do rito primeiro, data depois (operador 2026-08-13) ──────
  //
  // "As primeiras sejam aquelas que não foram triadas, e depois seguindo a ordem do
  // rito." O índice vem de `RITO_STAGES` do core — a ordem canônica JÁ EXISTE lá, com
  // `Aberta` e `Triagem` na frente. Redigitar a sequência aqui criaria a segunda
  // redação de uma regra com autoridade, e ela divergiria no primeiro estado novo.
  //
  // A ordenação por `openedAt` desc continua, agora como critério de DESEMPATE dentro
  // da fase. A nota do LT-01 abaixo segue valendo: sem ordem explícita os cards
  // embaralham em silêncio, porque a aparência cronológica era efeito colateral do
  // caseId time-prefixed.
  //
  // Fase ausente (projeção antiga) → fim da lista, nunca descartada: filtrar aqui
  // seria perda silenciosa de card.
  const indiceFase = (c: CaseListItem): number => {
    if (c.stage === undefined || c.stage === null) return Number.MAX_SAFE_INTEGER;
    const i = RITO_STAGES.indexOf(c.stage);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  const ordenados =
    cases === null
      ? null
      : [...cases].sort((a, b) => {
          const df = indiceFase(a) - indiceFase(b);
          if (df !== 0) return df;
          return (b.openedAt ?? -Infinity) - (a.openedAt ?? -Infinity);
        });

  const visiveis =
    ordenados === null
      ? null
      : ordenados.filter((c) => {
          if (soAguardando && c.aguardandoComite !== true) return false;
          // Busca por ID — `includes` case-insensitive: o membro cola um protocolo
          // parcial (o que aparece no card) e acha o caso. `trim` porque colar de
          // planilha traz espaço.
          const q = busca.trim().toUpperCase();
          if (q !== '' && !c.caseId.toUpperCase().includes(q)) return false;
          if (filtroStage === 'apuracao') {
            // Agrupamento: casa por `stateKind`, não por `stage`, porque o conjunto do
            // core é definido em estados (`AguardandoDecisao` e `AvaliacaoEmCurso` caem
            // na mesma coluna do board e precisam ser distinguidos aqui).
            if (!ESTADOS_EM_APURACAO.has(c.stateKind as never)) return false;
          } else if (filtroStage !== 'todas' && c.stage !== filtroStage) return false;
          if (filtroPend === 'minhas' && c.pendenteViewer !== true) return false;
          if (filtroPend === 'outros' && c.pendenteOutros !== true) return false;
          if (!dentroDoPeriodo(c.openedAt, filtroData, filtroMes, filtroAno)) return false;
          return true;
        });

  const handleLogout = async (): Promise<void> => {
    await logout();
    setSession(null);
    onLogout();
  };

  return (
    <div>
      <header className="dash-head">
        <h1 className="dash-head__title">{t('dashboard.title')}</h1>
        <div className="dash-head__actions">
          {session?.user.email && <span className="dash-head__user">{session.user.email}</span>}
          {/* Atualizar SEM F5. `disabled` enquanto carrega dá estado visível
              (opacity + not-allowed): um controle que parece clicável e não
              responde faz o membro achar que travou e apertar F5 — que é
              justamente o gesto que este botão existe para evitar. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void handleAtualizar()}
            disabled={atualizando}
            data-testid="dashboard-refresh"
          >
            {atualizando ? t('dashboard.refreshing') : t('dashboard.refresh')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => void handleLogout()}>
            {t('dashboard.logout')}
          </Button>
        </div>
      </header>

      {error && (
        <Banner tone="amber" role="alert" style={{ marginBottom: 16 }}>
          {error}
        </Banner>
      )}

      {cases === null && !error && (
        // Skeleton no lugar do <p>carregando</p> — mesmo grid/altura do card real
        // pra não haver salto de layout. role=status + aria-busy anunciam o load.
        <div className="case-grid" role="status" aria-busy="true" aria-label={t('app.loading')}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="cf-card cf-card--pad-md case-card case-card--skeleton"
              aria-hidden="true"
            >
              <span className="skeleton-line skeleton-line--title" />
              <span className="skeleton-line skeleton-line--id" />
              <span className="skeleton-line skeleton-line--badges" />
            </div>
          ))}
        </div>
      )}
      {/* i18n do comitê está represado (trabalho não commitado) — `defaultValue`
          entrega a copy correta agora e cede o lugar sozinho quando a chave entrar
          no pt-BR.json. Sem isto, a tela mostraria o nome da chave crua. */}
      {/* ── FILTROS E BUSCA (operador 2026-08-13) ─────────────────────────────
          Fora do `suportaSinal`: aquele guard protege SÓ o botão "aguardam
          resposta", que depende de uma projeção que pode não estar no ar. Busca por
          id e período funcionam com o que a lista sempre teve; status e pendência
          degradam sozinhos (campo ausente → o filtro não casa nada, e o `<option>`
          continua ali). Prender tudo atrás daquele guard esconderia a busca inteira
          por causa de um campo alheio. */}
      <div className="dash-filtros" data-testid="dash-filtros">
        <label className="dash-filtros__campo">
          <span className="dash-filtros__rotulo">
            {t('dashboard.filtro_busca_label', { defaultValue: 'Buscar por ID' })}
          </span>
          <input
            className="cf-input"
            type="search"
            value={busca}
            data-testid="filtro-busca"
            placeholder={t('dashboard.filtro_busca_ph', { defaultValue: 'ex.: TRIAGEMGATE' })}
            onChange={(e) => setBusca(e.target.value)}
          />
        </label>

        <label className="dash-filtros__campo">
          <span className="dash-filtros__rotulo">
            {t('dashboard.filtro_status_label', { defaultValue: 'Etapa do rito' })}
          </span>
          <select
            className="cf-input"
            value={filtroStage}
            data-testid="filtro-stage"
            onChange={(e) => setFiltroStage(e.target.value as RitoStage | 'todas' | 'apuracao')}
          >
            <option value="todas">
              {t('dashboard.filtro_todas', { defaultValue: 'Todas as etapas' })}
            </option>
            {/* Agrupamento, antes das etapas individuais: é o filtro mais usado
                ("o que está em andamento?") e não pertence à sequência abaixo. */}
            <option value="apuracao">{t('dashboard.filtro_em_apuracao')}</option>
            {/* Direto de `RITO_STAGES` — na ORDEM do rito, e impossível de divergir
                dela. Listar à mão deixaria o select fora de sincronia com a
                ordenação da lista no primeiro estado novo. */}
            {RITO_STAGES.map((s) => (
              <option key={s} value={s}>
                {RITO_STAGE_LABEL[s]}
              </option>
            ))}
          </select>
        </label>

        <label className="dash-filtros__campo">
          <span className="dash-filtros__rotulo">
            {t('dashboard.filtro_pendencia_label', { defaultValue: 'Pendência de validação' })}
          </span>
          <select
            className="cf-input"
            value={filtroPend}
            data-testid="filtro-pendencia"
            onChange={(e) => setFiltroPend(e.target.value as FiltroPendencia)}
          >
            <option value="todas">
              {t('dashboard.filtro_pend_todas', { defaultValue: 'Qualquer' })}
            </option>
            <option value="minhas">
              {t('dashboard.filtro_pend_minhas', { defaultValue: 'Aguardando MINHA validação' })}
            </option>
            <option value="outros">
              {t('dashboard.filtro_pend_outros', {
                defaultValue: 'Aguardando OUTROS membros',
              })}
            </option>
          </select>
        </label>

        <label className="dash-filtros__campo">
          <span className="dash-filtros__rotulo">
            {t('dashboard.filtro_data_label', { defaultValue: 'Data de abertura' })}
          </span>
          <input
            className="cf-input"
            type="date"
            value={filtroData}
            data-testid="filtro-data"
            onChange={(e) => setFiltroData(e.target.value)}
          />
        </label>

        <label className="dash-filtros__campo">
          <span className="dash-filtros__rotulo">
            {t('dashboard.filtro_mes_label', { defaultValue: 'Mês' })}
          </span>
          <select
            className="cf-input"
            value={filtroMes}
            data-testid="filtro-mes"
            onChange={(e) => setFiltroMes(e.target.value)}
          >
            <option value="">{t('dashboard.filtro_qualquer', { defaultValue: 'Qualquer' })}</option>
            {MESES.map((nome, i) => (
              <option key={nome} value={String(i + 1)}>
                {nome}
              </option>
            ))}
          </select>
        </label>

        <label className="dash-filtros__campo">
          <span className="dash-filtros__rotulo">
            {t('dashboard.filtro_ano_label', { defaultValue: 'Ano' })}
          </span>
          <input
            className="cf-input"
            type="number"
            inputMode="numeric"
            min={2020}
            max={2100}
            value={filtroAno}
            data-testid="filtro-ano"
            placeholder="ex.: 2026"
            onChange={(e) => setFiltroAno(e.target.value)}
          />
        </label>

        {suportaSinal && (
          <Button
            variant={soAguardando ? 'primary' : 'ghost'}
            size="sm"
            data-testid="filtro-aguardando"
            aria-pressed={soAguardando}
            onClick={() => setSoAguardando((v) => !v)}
          >
            {t('dashboard.filtro_aguardando', { defaultValue: 'Só os que aguardam resposta' })}
          </Button>
        )}

        {/* LIMPAR só aparece com algum filtro ativo. Um botão sempre visível que às
            vezes não faz nada ensina a ignorá-lo. */}
        {filtrosAtivos && (
          <Button variant="ghost" size="sm" data-testid="filtro-limpar" onClick={limparFiltros}>
            {t('dashboard.filtro_limpar', { defaultValue: 'Limpar filtros' })}
          </Button>
        )}
      </div>

      {/* 🔴 TESTEMUNHA DO FILTRO. Sem esta linha, "0 de 12" e "nenhuma denúncia" são a
          MESMA tela — e o membro conclui que não há caso quando há doze escondidos por
          um filtro que ele esqueceu. É o §"fallback plausível esconde ausência" na
          forma de lista vazia: some contagem, some a explicação. */}
      {visiveis !== null && cases !== null && cases.length > 0 && (
        <p className="dash-filtros__contagem" role="status" data-testid="dash-contagem">
          {/* `count` (não `n`) porque a chave usa o PLURAL do i18next: com 1 caso a
              frase tem de ser "1 de 12 denúncia". O nome do parâmetro não é livre —
              é `count` que o i18next lê para escolher `_one`/`_other`. */}
          {t('dashboard.contagem', {
            count: visiveis.length,
            total: cases.length,
          })}
          {filtrosAtivos &&
            ` · ${t('dashboard.contagem_filtrada', { defaultValue: 'filtro ativo' })}`}
        </p>
      )}

      {cases !== null && cases.length === 0 && (
        <Banner tone="info" role="status">
          {t('dashboard.empty')}
        </Banner>
      )}

      {visiveis !== null && cases !== null && cases.length > 0 && visiveis.length === 0 && (
        <Banner tone="info" role="status" data-testid="aguardando-vazio">
          {/* 🔴 A MENSAGEM DEPENDE DO FILTRO, e antes não dependia.
              Este banner dizia sempre "Nenhum caso aguardando resposta do comitê" —
              correto quando `soAguardando` era o ÚNICO filtro. Com busca, etapa,
              pendência e período, a mesma frase passaria a MENTIR: quem filtrou por
              data leria uma afirmação sobre resposta do denunciante.
              Copy específica só no caso específico; nos outros, a frase genérica diz
              a verdade (há casos, o filtro os esconde) e o botão de limpar está ao
              lado. */}
          {soAguardando && !buscaOuPeriodoAtivo
            ? t('dashboard.aguardando_vazio', {
                defaultValue: 'Nenhum caso aguardando resposta do comitê.',
              })
            : t('dashboard.filtro_vazio', {
                defaultValue:
                  'Nenhuma denúncia corresponde aos filtros. Ajuste ou limpe os filtros.',
              })}
        </Banner>
      )}

      {visiveis !== null && visiveis.length > 0 && (
        <div className="case-grid">
          {visiveis.map((c) => (
            <button
              key={c.caseId}
              type="button"
              className="cf-card cf-card--pad-md cf-card--interactive case-card"
              onClick={() => onSelectCase(c.caseId)}
            >
              {/* Título legível: a categoria é a headline; o caseId vira etiqueta
                  mono secundária e discreta (design v2). */}
              <span className="case-card__cat">{categoriaLabel(t, c.categoria)}</span>
              <span className="case-card__id">{c.caseId.slice(0, 12)}…</span>
              <span className="case-card__badges">
                <Badge tone={stateTone(c.stateKind)}>{estadoLabelComite(c.stateKind)}</Badge>
                {/* D2 — ADITIVO: entra ao lado dos badges que já existiam, nunca no
                    lugar deles. `coral` porque é pendência do comitê, não informação. */}
                {c.aguardandoComite === true && (
                  <Badge
                    tone="coral"
                    data-testid="badge-aguardando-comite"
                    aria-label={t('dashboard.aguardando_comite_aria', {
                      defaultValue: 'Este caso tem mensagem do denunciante sem resposta',
                    })}
                  >
                    {t('dashboard.aguardando_comite', {
                      defaultValue: 'Aguardando resposta do comitê',
                    })}
                  </Badge>
                )}
                {comiteCompetencia(c.categoria) !== null && (
                  <Badge tone="info">{t(`competencia.${comiteCompetencia(c.categoria)}`)}</Badge>
                )}
                {(() => {
                  // Badge de prazo (SLA soft) — derivado dos anchors projetados.
                  const prazo = resolverPrazo(
                    {
                      stateKind: c.stateKind,
                      openedAt: c.openedAt,
                      classifiedAt: c.classifiedAt,
                      apuracaoEncerradaAt: c.apuracaoEncerradaAt ?? null,
                      decisaoEmittedAt: c.decisaoEmittedAt,
                      prorrogada: c.investigacaoProrrogada,
                    },
                    Date.now(),
                    FERIADOS_NACIONAIS,
                  );
                  if (prazo === null) return null;
                  const texto =
                    prazo.status === 'no_prazo'
                      ? t('prazo.no_prazo')
                      : prazo.status === 'vencendo'
                        ? t('prazo.vence_em', { dias: prazo.diasRestantes })
                        : t('prazo.vencido_ha', { dias: Math.abs(prazo.diasRestantes) });
                  return <Badge tone={prazoTone(prazo.status)}>{texto}</Badge>;
                })()}
              </span>
              <span className="case-card__meta">
                {/* Data e hora da abertura (decisão do operador 2026-08-13). Mostrava
                    `createdWeek` — semana — enquanto o `openedAt` estava no MESMO
                    objeto, usado só para ordenar. `Encerrada` perde o campo, e aí a
                    semana é o que realmente se sabe: fallback declarado. */}
                {c.openedAt !== null
                  ? new Date(c.openedAt).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : c.createdWeek}{' '}
                · {t('dashboard.anexos_short', { count: c.attachmentsCount })}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
